import {createTheme, ThemeOptions} from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {AutocompleteClassKey, AutocompleteProps} from '@material-ui/lab/Autocomplete';
import {MuiPickersOverrides} from '@material-ui/pickers/typings/overrides';
import {createElement} from 'react';
import {
    WHITE,
    BACKGROUND_PRIMARY,
    PRIMARY_RED,
    TEXT_PRIMARY,
    TEXT_SECONDARY,
    BLACK,
    VZ_BLACK,
    INPUT_TEXT_PRIMARY,
} from '../const/colors';
import { DEFAULT } from '../const/fontSizes';

type overridesNameToClassKey = {
    [P in keyof MuiPickersOverrides]: keyof MuiPickersOverrides[P];
};

declare module '@material-ui/core/styles/overrides' {
    export interface ComponentNameToClassKey extends overridesNameToClassKey {
        MuiAutocomplete: AutocompleteClassKey;
    }
}

declare module '@material-ui/core/styles/props' {
    interface ComponentsPropsList {
        MuiAutocomplete: Partial<AutocompleteProps<any, any, any, any>>;
    }
}

const defaultThemeOptions: ThemeOptions = {
    spacing: 16,
    shape: {
        borderRadius: 3,
    },
    props: {
        MuiButton: {
            disableElevation: true,
        },
        MuiSelect: {
            IconComponent: ExpandMoreIcon,
        },
        MuiNativeSelect: {
            IconComponent: ExpandMoreIcon,
        },
        MuiAutocomplete: {
            popupIcon: createElement(ExpandMoreIcon),
        }
    },
    palette: {
        primary: {
            main: VZ_BLACK,
        },
        secondary: {
            main: PRIMARY_RED,
        },
        text: {
            primary: TEXT_PRIMARY,
            secondary: TEXT_SECONDARY,
        },
        background: {
            default: BACKGROUND_PRIMARY,
        },
    },
    typography: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: DEFAULT,
        h1: {
            fontWeight: 700,
            fontSize: 56,
        },
        h3: {
            fontWeight: 700,
            fontSize: 36,
        },
    },
    overrides: {
        MuiCssBaseline: {
            '@global': {
                p: {
                    fontSize: DEFAULT,
                },
            },
        },
        MuiLink: {
            root: {
                fontSize: DEFAULT,
            }
        },
        MuiOutlinedInput: {
            notchedOutline: {
                '& legend > span': {
                    letterSpacing: '0.05em',
                },
            },
        },
        MuiTypography: {
            paragraph: {
                fontSize: DEFAULT,
            },
            body1: {
                fontSize: DEFAULT,
            },
            caption: {
                fontWeight: 'bold',
                color: 'black',
            },
        },
        MuiPickersCalendarHeader: {
            iconButton: {
                color: PRIMARY_RED,
            },
        },
        MuiPickersCalendar: {
            transitionContainer: {
                minHeight: '254px',
            },
        },
        MuiPickersDay: {
            day: {
                borderColor: '#e0e0e0',
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 0,
                margin: 0,
                width: '40px',
                height: '40px',
            },
            daySelected: {
                backgroundColor: PRIMARY_RED,
                borderColor: PRIMARY_RED,
            },
            current: {
                borderColor: BLACK,
                borderWidth: 2,
            },
            dayDisabled: {
                backgroundColor: '#F5F5F5',
                color: '#BDBDBD',
            },
        },
        MuiInputBase: {
            root: {
                width: 300,
                height: 56,
                minHeight: 56, //for multiline
                fontSize: DEFAULT,
                color: INPUT_TEXT_PRIMARY,
                '&.MuiAutocomplete-inputRoot.MuiInputBase-multiline': {
                    // multiline is broken, by setting fixed width,
                    // paddings change makes MuiInputBase inside Autocomplete having 48px height
                    height: 'auto',
                    paddingTop: '6px!important',
                    paddingBottom: '0!important',
                },
                '&.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input':
                    {
                        // backgroundColor: 'red',
                        padding: '0px',
                    },
            },
        },
        MuiSelect: {
            select: {
                padding: '0 17px',
            },
            icon: {
                transform: 'translateY(-5%)',
                fontSize: 26,
            },
            iconOpen: {
                transform: 'translateY(-5%) rotate(180deg)',
                fontSize: 26,
            },
            selectMenu: {
                height: '100%',
                display: 'flex',
                alignItems: 'center',
            },
        },
        MuiAutocomplete: {
            popupIndicator: {
                transform: 'translateY(-5%)',
            },
            popupIndicatorOpen: {
                transform: 'translateY(-5%) rotate(180deg)',
            },
            clearIndicatorDirty: {
                transform: 'translateY(-5%)',
            },
            option: {
                fontSize: DEFAULT,
                height: 48,
            }
        },
        MuiTableCell: {
            head: {
                fontWeight: 'bold',
                fontSize: 20,
            },
        },
        MuiButton: {
            root: {
                boxShadow: 'none',
                borderRadius: 256,
                height: 56,
                textTransform: 'none',
                fontSize: DEFAULT,
                padding: '0 24px',
            },
            contained: {
                fontWeight: 'bold',
                boxShadow: 'none',
                '&:hover': {
                    boxShadow: 'none',
                },
                '&:focus': {
                    boxShadow: 'none',
                },
                '&:active': {
                    boxShadow: 'none',
                },
            },
        },
        MuiMenuItem: {
            root: {
                fontSize: DEFAULT,
                height: 48,
            }
        },
    },
};

const alternativeThemeOptions = {
    ...defaultThemeOptions,
    palette: {
        ...defaultThemeOptions.palette,
        background: {
            default: WHITE,
        },
    },
};

export const defaultTheme = createTheme(defaultThemeOptions);

export const alternativeTheme = createTheme(alternativeThemeOptions);
